// Dev-only console patch: mirrors console.{log,info,warn,error,debug} to a
// dev-server endpoint that appends to ./console.log. No-op outside development.

const ENDPOINT = '/__console-log';
const FLUSH_MS = 250;
const LEVELS = [ 'log', 'info', 'warn', 'error', 'debug' ];

const safeStringify = ( value ) => {
  if( typeof value === 'string' ) return value;
  const seen = new WeakSet();
  try {
    return JSON.stringify( value, ( _key, val ) => {
      if( val instanceof Error ) return `${val.name}: ${val.message}\n${val.stack || ''}`;
      if( typeof val === 'object' && val !== null ) {
        if( seen.has( val ) ) return '[Circular]';
        seen.add( val );
      }
      if( typeof val === 'function' ) return `[Function ${val.name || 'anonymous'}]`;
      if( typeof val === 'undefined' ) return '[undefined]';
      return val;
    } );
  } catch {
    try { return String( value ); } catch { return '[unserializable]'; }
  }
};

const initDevConsoleLogger = () => {
  if( process.env.NODE_ENV !== 'development' ) return;
  if( typeof window === 'undefined' ) return;
  if( window.__devConsoleLoggerInstalled ) return;
  window.__devConsoleLoggerInstalled = true;

  const queue = [];
  let flushing = false;
  let timer = null;

  const flush = async () => {
    if( flushing || queue.length === 0 ) return;
    flushing = true;
    const entries = queue.splice( 0, queue.length );
    try {
      await fetch( ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify( { entries } ),
        keepalive: true,
      } );
    } catch {
      // Swallow: don't break the app if the dev endpoint is unreachable.
    } finally {
      flushing = false;
    }
  };

  const schedule = () => {
    if( timer ) return;
    timer = setTimeout( () => {
      timer = null;
      flush();
    }, FLUSH_MS );
  };

  LEVELS.forEach( ( level ) => {
    const original = window.console[ level ].bind( window.console );
    window.console[ level ] = ( ...args ) => {
      original( ...args );
      try {
        queue.push( {
          t: new Date().toISOString(),
          level,
          msg: args.map( safeStringify ).join( ' ' ),
        } );
        schedule();
      } catch {
        // Never throw from console.
      }
    };
  } );

  window.addEventListener( 'error', ( e ) => {
    queue.push( {
      t: new Date().toISOString(),
      level: 'error',
      msg: `window.onerror: ${e.message} @ ${e.filename}:${e.lineno}:${e.colno}\n${e.error && e.error.stack ? e.error.stack : ''}`,
    } );
    schedule();
  } );

  window.addEventListener( 'unhandledrejection', ( e ) => {
    queue.push( {
      t: new Date().toISOString(),
      level: 'error',
      msg: `unhandledrejection: ${safeStringify( e.reason )}`,
    } );
    schedule();
  } );

  window.addEventListener( 'beforeunload', flush );
};

export default initDevConsoleLogger;
